<template>
    <div v-if="activeAssignment" class="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200">
        <!-- Header -->
        <div class="p-4 border-b border-gray-100">
            <div class="flex items-center justify-between">
                <div class="flex items-center space-x-3">
                    <h3 class="text-lg font-medium text-gray-900" v-text="name"></h3>
                    <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" v-text="assignment.document_type"></span>
                    <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        <a :href="order.path" class="text-blue-800 hover:text-blue-900" target="_blank">Order #{{ order.number }}</a>
                    </span>
                </div>
                <div class="flex items-center space-x-6">
                    <div class="flex items-center space-x-2 text-sm text-gray-500">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span v-text="date"></span>
                    </div>
                    <div class="text-sm text-gray-500" v-text="lastProblem.created_ago"></div>
                    <div v-if="isAdmin" class="text-sm font-medium text-indigo-600" v-text="coder"></div>
                </div>
            </div>
        </div>

        <!-- Content -->
        <div class="p-4 space-y-4">
            <!-- Attachments Section -->
            <div>
                <div v-if="showAttachments" class="space-y-3">
                    <div class="flex items-center">
                        <button @click="toggleShowAttachments" class="inline-flex items-center text-gray-500 hover:text-gray-700">
                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                            </svg>
                            <span class="ml-2 text-sm font-medium">Attachments</span>
                        </button>
                    </div>
                    <div class="pl-7">
                        <handle-attachments 
                            name="attachments" 
                            :assignment="assignment" 
                            :upload="upload" 
                            @toggled="toggleAttachment">
                        </handle-attachments>
                    </div>
                </div>
                <div v-else>
                    <button @click="toggleShowAttachments" class="inline-flex items-center text-gray-500 hover:text-gray-700">
                        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                        </svg>
                        <span class="ml-2 text-sm font-medium">Show Attachments</span>
                    </button>
                </div>
            </div>

            <!-- Comments Section -->
            <div class="border-t border-gray-100 pt-4">
                <div v-if="showComments" class="space-y-4">
                    <div class="flex items-center">
                        <button @click="toggleShowComments" class="inline-flex items-center text-gray-500 hover:text-gray-700">
                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                            </svg>
                            <span class="ml-2 text-sm font-medium">Comments</span>
                        </button>
                    </div>
                    <div class="pl-7">
                        <problem :problems="assignment.problems">
                            <div class="mt-2">
                                <textarea 
                                    v-model="resolution" 
                                    rows="4" 
                                    class="block w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                    placeholder="Add your resolution comment here..."
                                    required>
                                </textarea>
                            </div>
                        </problem>
                    </div>
                </div>
                <div v-else>
                    <button @click="toggleShowComments" class="inline-flex items-center text-gray-500 hover:text-gray-700">
                        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                        </svg>
                        <span class="ml-2 text-sm font-medium">Show Comments</span>
                    </button>
                </div>
            </div>

            <!-- Action Buttons -->
            <div v-if="!loading" class="flex justify-end pt-4 border-t border-gray-100 space-x-3" >
                    <button 
                        v-if="order.submitted_at"
                        @click="moveToNewOrder"
                        class="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                        Move To New Order
                    </button>

                <form @submit.prevent="deleteAssignment" class="inline-flex">
                    <button 
                        type="submit"
                        class="inline-flex items-center px-4 py-2 border border-red-300 shadow-sm text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500">
                        Delete File
                    </button>
                </form>

                <button 
                    @click="resolve" 
                    class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                    Mark as Resolved
                </button>
            </div>
            <div v-else>
                <div class="flex justify-center items-center h-24">
                    <svg class="w-6 h-6 text-gray-500 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                </div>
            </div>
        </div>
    </div>
    <div v-else>
        Assignment deleted.
    </div>
</template>

<script>
    import moment from 'moment';
    import HandleAttachments from './HandleAttachments.vue';
    import Problem from './Problem.vue';
    export default {
        components: {HandleAttachments, Problem},
        props: ['assignment', 'coder', 'isAdmin'],
        data() {
            return {
                name: this.assignment.name,
                showAttachments: false,
                showComments: true,
                upload: true,
                resolution: '',
                order: this.assignment.order,
                activeAssignment: this.assignment,
                loading: false,
            }
        },
        computed: {
            comment: function() {
                return this.assignment.problems.slice(-1)[0].comment; // get last comment
            },
            lastProblem: function() {
                return this.assignment.problems.slice(-1)[0];
            },
            date: function() {
                return moment.utc(this.assignment.date).format('MMM DD, YYYY');
            }
        },
        methods: {
            // allowUpload() {
            //     this.upload = true
            // },
            toggleAttachment() {
                // return this.upload = ! this.upload;
                return this.upload = true; //Alwasy on for now
            },
            toggleShowAttachments() {
                return this.showAttachments = ! this.showAttachments;
            },
            toggleShowComments() {
                return this.showComments = ! this.showComments;
            },

            resolve() {
                if(this.resolution.length <= 3) {
                    // alert('Resolution must be filled out for this problem assignment.');
                    this.$toast.error('Resolution must be filled out for this problem assignment. Must be at least 3 characters.');
                    return false;
                }
                this.loading = true;
                this.$toast.info('Marking problem assignment as resolved...');
                axios.patch('/problems/'+this.lastProblem.id, {'resolved_comment': this.resolution})
                    .then(({data}) => {
                        this.$toast.success('Problem assignment marked as resolved.');
                        location.reload();
                        // this.data.slice(-1)[0] = data;
                    })
                    .finally(() => {
                        this.loading = false;
                    });
            },

            moveToNewOrder() {
                this.loading = true;
                // this.$toast.info('Moving assignment to new order...');
                axios.patch(`/assignments/${this.assignment.id}/move/orders`)
                    .then(({data}) => {
                        if (data.success) {
                            this.$toast.success(`Assignment moved to Order #${data.order.number}`);
                            this.order = data.order;
                        }
                    })
                    .catch(error => {
                        this.$toast.error('Failed to move assignment');
                        console.error('Error moving assignment:', error);
                    })
                    .finally(() => {
                        this.loading = false;
                    });
            },

            deleteAssignment() {
                if (!confirm('Are you sure you want to delete this assignment?')) {
                    return;
                }
                this.loading = true;
                // this.$toast.info('Deleting assignment...');
                axios.delete(`/assignments/${this.assignment.id}`)
                    .then(() => {
                        this.$toast.success('Assignment deleted successfully');
                        this.activeAssignment = null;
                        // window.location.reload();

                    })
                    .catch(error => {
                        this.$toast.error('Failed to delete assignment');
                        console.error('Error moving assignment:', error);
                    })
                    .finally(() => {
                        this.loading = false;
                    });
            }
        }
    }
</script>
